/**
 * AgentCoachingDetail — Full agent workspace for the coaching tab.
 * Replaces the single-panel view with a tabbed agent detail page.
 */

import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChartLine, faSpinner, faCheckCircle } from '@fortawesome/free-solid-svg-icons';
import { colors, fonts } from '../broker-intelligence/shared/colors';
import axiosInstance from '../../helpers/api';
import toast from 'react-hot-toast';

interface Props {
  agentId: string;
  agentName: string;
  agentEmail: string;
  coachData: any;
  retentionDeals: any;
  pastClosings: any;
  setPastClosings: (d: any) => void;
  coachPrompts: string[];
  setCoachPrompts: (p: string[]) => void;
  coachChat: Array<{ role: string; text: string }>;
  setCoachChat: (c: any) => void;
}

/** Lightweight markdown → HTML for AI chat responses */
function formatCoachMarkdown(text: string): string {
  if (!text) return '';
  return text
    .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
    .replace(/^### (.+)$/gm, '<h4 style="margin:12px 0 6px;font-size:13px;color:#0F1E35;">$1</h4>')
    .replace(/^## (.+)$/gm, '<h3 style="margin:14px 0 8px;font-size:14px;font-weight:700;color:#0F1E35;">$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/^[•\-]\s+(.+)$/gm, '<li style="margin:3px 0;margin-left:16px;">$1</li>')
    .replace(/`([^`]+)`/g, '<code style="background:#f0f0f0;padding:1px 5px;border-radius:3px;font-size:12px;">$1</code>')
    .replace(/\n\n/g, '<br/><br/>')
    .replace(/<\/li>\n(?!<li)/g, '</li>')
    .replace(/\n/g, '<br/>');
}

const AgentCoachingDetail: React.FC<Props> = ({
  agentId, agentName, agentEmail, coachData, retentionDeals, pastClosings, setPastClosings,
  coachPrompts, setCoachPrompts, coachChat, setCoachChat,
}) => {
  const [subTab, setSubTab] = useState<'overview' | 'deals' | 'lost' | 'chat' | 'gameplan' | 'notes'>('overview');
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const [notes, setNotes] = useState<Array<{ id: string; text: string; type: string; date: string }>>([]);
  const [newNote, setNewNote] = useState('');
  const [noteType, setNoteType] = useState('call');
  const [editingContact, setEditingContact] = useState(false);
  const [contactInfo, setContactInfo] = useState({ email: agentEmail, phone: '' });

  // Load notes + contact info
  useEffect(() => {
    axiosInstance.get(`/sponsor/coaching/${agentId}/notes`).then(({ data }) => {
      if (data.success) {
        setNotes(data.notes || []);
        if (data.phone) setContactInfo(prev => ({ ...prev, phone: data.phone }));
      }
    }).catch(() => {});
  }, [agentId]);

  const production = coachData?.production || {};
  const retention = coachData?.retention || {};
  const mlsId = coachData?.agent?.mlsId || '';

  const sendChat = (msg: string) => {
    if (!msg.trim()) return;
    setCoachChat((prev: any) => [...prev, { role: 'sponsor', text: msg }]);
    setChatInput('');
    setChatLoading(true);
    axiosInstance.post(`/sponsor/coaching/${agentId}/chat`, { message: msg, coachData }).then(({ data }: any) => {
      if (data.response) setCoachChat((prev: any) => [...prev, { role: 'ai', text: data.response }]);
    }).catch(() => setCoachChat((prev: any) => [...prev, { role: 'ai', text: 'Sorry, try again.' }])).finally(() => setChatLoading(false));
  };

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sticky Header + Tabs */}
      <div style={{ background: '#fff', padding: '16px 24px', borderBottom: `1px solid ${colors.border}`, flexShrink: 0 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12 }}>
          <div>
            <h3 style={{ fontFamily: fonts.display, fontSize: 22, color: colors.navy, margin: 0 }}>{agentName}</h3>
            {coachData?.agent?.brokerage && <div style={{ fontSize: 13, fontWeight: 600, color: colors.gold }}>{coachData.agent.brokerage}</div>}
            {!editingContact ? (
              <div style={{ fontSize: 12, color: colors.muted, cursor: 'pointer' }} onClick={() => setEditingContact(true)}>
                {contactInfo.email || agentEmail} {contactInfo.phone ? `· ${contactInfo.phone}` : ''} {mlsId ? `· MLS: ${mlsId}` : ''} <span style={{ fontSize: 10, color: colors.gold }}>✎</span>
              </div>
            ) : (
              <div style={{ display: 'flex', gap: 6, alignItems: 'center', marginTop: 4 }}>
                <input value={contactInfo.email} onChange={e => setContactInfo({ ...contactInfo, email: e.target.value })} placeholder="Email" style={{ padding: '3px 8px', fontSize: 11, borderRadius: 4, border: '1px solid #ddd', width: 180 }} />
                <input value={contactInfo.phone} onChange={e => setContactInfo({ ...contactInfo, phone: e.target.value })} placeholder="Phone" style={{ padding: '3px 8px', fontSize: 11, borderRadius: 4, border: '1px solid #ddd', width: 120 }} />
                <button onClick={async () => {
                  try {
                    await axiosInstance.post(`/sponsor/coaching/${agentId}/update-contact`, contactInfo);
                    toast.success('Contact updated');
                    setEditingContact(false);
                  } catch { toast.error('Failed to save'); }
                }} style={{ padding: '3px 10px', fontSize: 10, fontWeight: 700, borderRadius: 4, border: 'none', background: colors.gold, color: colors.navy, cursor: 'pointer' }}>Save</button>
                <button onClick={() => setEditingContact(false)} style={{ padding: '3px 8px', fontSize: 10, borderRadius: 4, border: '1px solid #ccc', background: '#fff', color: '#888', cursor: 'pointer' }}>Cancel</button>
              </div>
            )}
          </div>
          {production.totalDeals > 0 && (
            <div style={{ textAlign: 'right' }}>
              <div style={{ fontSize: 18, fontWeight: 700, color: colors.navy }}>{production.totalDeals} deals</div>
              <div style={{ fontSize: 12, color: colors.muted }}>${Math.round((production.totalVolume || 0) / 1000000 * 10) / 10}M volume</div>
            </div>
          )}
        </div>
        <div style={{ display: 'flex', gap: 4 }}>
          {[
            { key: 'overview', label: 'Overview' },
            { key: 'deals', label: 'Past Deals' },
            { key: 'lost', label: `Lost Clients${retention.lostDeals ? ` (${retention.lostDeals})` : ''}` },
            { key: 'notes', label: `Notes${notes.length ? ` (${notes.length})` : ''}` },
            { key: 'chat', label: 'AI Strategy' },
            { key: 'gameplan', label: 'Game Plan' },
          ].map(tab => (
            <button key={tab.key} onClick={() => setSubTab(tab.key as any)}
              style={{ padding: '6px 14px', fontSize: 11, fontWeight: 600, borderRadius: 6, border: 'none', cursor: 'pointer',
                background: subTab === tab.key ? colors.gold : '#f0f0f0',
                color: subTab === tab.key ? colors.navy : '#888'
              }}>
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* Tab Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: 24, background: colors.offBg }}>

        {/* ─── OVERVIEW ─── */}
        {subTab === 'overview' && (
          <>
            {/* Production Stats */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Total Deals', value: production.totalDeals || 0 },
                { label: 'Volume', value: '$' + Math.round((production.totalVolume || 0) / 1000000 * 10) / 10 + 'M' },
                { label: 'Buyer %', value: (production.buyerPct || 0) + '%' },
                { label: 'Listing %', value: (production.sellerPct || 0) + '%' },
                { label: 'Avg Price', value: '$' + Math.round((production.avgPrice || 0) / 1000) + 'K' },
              ].map((s, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 8, padding: 12, textAlign: 'center', border: `1px solid ${colors.border}` }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: colors.navy }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Retention Summary */}
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 16 }}>
              {[
                { label: 'Past Buyers', value: retention.pastBuyers || 0, color: '#27ae60' },
                { label: 'Past Sellers', value: retention.pastSellers || 0, color: '#e67e22' },
                { label: 'Lost Deals', value: retention.lostDeals || 0, color: '#c62828' },
                { label: 'Lost Revenue', value: '$' + (retention.lostRevenueEstimate || 0).toLocaleString(), color: '#c62828' },
              ].map((s, i) => (
                <div key={i} style={{ background: '#fff', borderRadius: 8, padding: 12, textAlign: 'center', borderTop: `3px solid ${s.color}` }}>
                  <div style={{ fontSize: 18, fontWeight: 700, color: colors.navy }}>{s.value}</div>
                  <div style={{ fontSize: 10, color: '#888' }}>{s.label}</div>
                </div>
              ))}
            </div>

            {/* Retention Alerts */}
            {retention.triggers?.length > 0 && (
              <div style={{ background: '#fff', borderRadius: 12, padding: 16, marginBottom: 16, border: '1px solid #f8bbd0' }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: '#c62828', marginBottom: 8 }}>Retention Alerts</div>
                {retention.triggers.map((t: any, i: number) => (
                  <div key={i} style={{ fontSize: 12, color: '#555', marginBottom: 4 }}>
                    {t.type === 'lost_listing' ? '📉' : '🏠'} {t.address} — {
                      t.type === 'lost_listing' ? `Lost to ${t.competitor || 'another agent'} ($${(t.lostCommission || 0).toLocaleString()})` : t.type
                    }
                  </div>
                ))}
              </div>
            )}

            {/* Quick Actions */}
            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
              <button onClick={() => setSubTab('deals')} style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: `1px solid ${colors.border}`, background: '#fff', color: colors.navy, cursor: 'pointer' }}>View All Deals →</button>
              <button onClick={() => setSubTab('lost')} style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: '1px solid #f8bbd0', background: '#fff', color: '#c62828', cursor: 'pointer' }}>View Lost Clients →</button>
              <button onClick={() => setSubTab('chat')} style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: 'none', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy, cursor: 'pointer' }}>Ask AI About This Agent →</button>
              <button onClick={() => setSubTab('gameplan')} style={{ padding: '8px 16px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: `1px solid ${colors.gold}`, background: '#fff', color: colors.gold, cursor: 'pointer' }}>Generate Game Plan →</button>
            </div>
          </>
        )}

        {/* ─── DEALS ─── */}
        {subTab === 'deals' && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px' }}>All Closed Transactions</h4>
            {!pastClosings?.recent_deals?.length ? (
              <button onClick={async () => {
                try {
                  const { data } = await axiosInstance.get(`/sponsor/coaching/${agentId}/transactions`);
                  if (data.success) setPastClosings({ ...data, recent_deals: data.transactions, total_deals: data.total });
                } catch { }
              }} style={{ padding: '10px 24px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy }}>
                Load All Transactions
              </button>
            ) : (
              <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                <div style={{ fontSize: 12, color: '#888', marginBottom: 8 }}>{pastClosings.total_deals || pastClosings.recent_deals.length} transactions</div>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ borderBottom: '2px solid #e9ecef' }}>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: colors.navy }}>Property</th>
                    <th style={{ padding: '6px 8px', textAlign: 'center', color: colors.navy }}>Side</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', color: colors.navy }}>Price</th>
                    <th style={{ padding: '6px 8px', textAlign: 'left', color: colors.navy }}>Date</th>
                    <th style={{ padding: '6px 8px', textAlign: 'right', color: colors.navy }}>DOM</th>
                  </tr></thead>
                  <tbody>{pastClosings.recent_deals.map((d: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '6px 8px' }}>
                        <div style={{ fontWeight: 600, color: '#333' }}>{d.address}</div>
                        <div style={{ fontSize: 9, color: '#999' }}>{d.city}{d.beds ? ` · ${d.beds}bd/${d.baths}ba` : ''}{d.sqft ? ` · ${Number(d.sqft).toLocaleString()} sqft` : ''}</div>
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'center' }}>
                        <span style={{ padding: '2px 8px', borderRadius: 10, fontSize: 10, fontWeight: 600, background: d.side === 'buyer' ? '#e3f2fd' : '#fff3e0', color: d.side === 'buyer' ? '#1565c0' : '#e65100' }}>{d.side === 'buyer' ? 'Buyer' : 'Listing'}</span>
                      </td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', fontWeight: 600, color: colors.navy }}>${Math.round(Number(d.price) / 1000)}K</td>
                      <td style={{ padding: '6px 8px', color: '#666' }}>{d.date ? new Date(d.date).toLocaleDateString('en-US', { month: 'short', year: 'numeric' }) : ''}</td>
                      <td style={{ padding: '6px 8px', textAlign: 'right', color: '#666' }}>{d.dom || '—'}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            )}
          </div>
        )}

        {/* ─── LOST CLIENTS ─── */}
        {subTab === 'lost' && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: '#c62828', margin: '0 0 4px' }}>Client Retention — Lost Deals</h4>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>Past buyers who listed with a different agent. Each is a missed listing + missed loan opportunity for you.</p>
            {retentionDeals?.deals?.length > 0 ? (
              <div style={{ maxHeight: 500, overflowY: 'auto' }}>
                <table style={{ width: '100%', fontSize: 11, borderCollapse: 'collapse' }}>
                  <thead><tr style={{ borderBottom: '2px solid #e9ecef' }}>
                    <th style={{ padding: '6px', textAlign: 'left' }}>Property</th>
                    <th style={{ padding: '6px', textAlign: 'right' }}>Purchase</th>
                    <th style={{ padding: '6px', textAlign: 'right' }}>Resale</th>
                    <th style={{ padding: '6px', textAlign: 'left' }}>Listed By</th>
                    <th style={{ padding: '6px', textAlign: 'right' }}>Lost $</th>
                  </tr></thead>
                  <tbody>{retentionDeals.deals.filter((d: any) => Number(d.purchase_price || d.purchasePrice || 0) >= 25000).map((d: any, i: number) => (
                    <tr key={i} style={{ borderBottom: '1px solid #f0f0f0' }}>
                      <td style={{ padding: '6px' }}>
                        <div style={{ fontWeight: 600 }}>{d.property_address || d.propertyAddress}</div>
                        <div style={{ fontSize: 9, color: '#888' }}>{d.city}</div>
                      </td>
                      <td style={{ padding: '6px', textAlign: 'right', fontSize: 10 }}>${Math.round(Number(d.purchase_price || d.purchasePrice || 0) / 1000)}K</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontSize: 10 }}>${Math.round(Number(d.resale_price || d.resalePrice || 0) / 1000)}K</td>
                      <td style={{ padding: '6px', fontSize: 10 }}>{d.competitor_agent_name || d.competitorAgentName || '—'}</td>
                      <td style={{ padding: '6px', textAlign: 'right', fontWeight: 700, color: '#c62828' }}>${Math.round(Number(d.lost_commission_cents || d.lostCommissionCents || 0) / 100).toLocaleString()}</td>
                    </tr>
                  ))}</tbody>
                </table>
              </div>
            ) : (
              <div style={{ color: '#888', fontSize: 13 }}>No lost deal data available for this agent.</div>
            )}
          </div>
        )}

        {/* ─── NOTES & ACTIVITY ─── */}
        {subTab === 'notes' && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 16px' }}>Notes & Activity — {agentName}</h4>

            {/* Add Note */}
            <div style={{ marginBottom: 16, background: '#f8f9fa', borderRadius: 8, padding: 12 }}>
              <div style={{ display: 'flex', gap: 6, marginBottom: 8 }}>
                {[
                  { key: 'call', label: '📞 Call' },
                  { key: 'meeting', label: '☕ Meeting' },
                  { key: 'email', label: '✉️ Email' },
                  { key: 'thankyou', label: '💌 Thank You' },
                  { key: 'note', label: '📝 Note' },
                ].map(t => (
                  <button key={t.key} onClick={() => setNoteType(t.key)}
                    style={{ padding: '4px 10px', fontSize: 10, fontWeight: 600, borderRadius: 12, border: 'none', cursor: 'pointer',
                      background: noteType === t.key ? colors.gold : '#e0e0e0',
                      color: noteType === t.key ? colors.navy : '#666'
                    }}>{t.label}</button>
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input value={newNote} onChange={e => setNewNote(e.target.value)}
                  onKeyDown={e => {
                    if (e.key === 'Enter' && newNote.trim()) {
                      axiosInstance.post(`/sponsor/coaching/${agentId}/notes`, { text: newNote.trim(), type: noteType }).then(({ data }) => {
                        if (data.success) { setNotes(prev => [data.note, ...prev]); setNewNote(''); toast.success('Note saved'); }
                      }).catch(() => toast.error('Failed to save'));
                    }
                  }}
                  placeholder={`Log a ${noteType}...`}
                  style={{ flex: 1, padding: '8px 12px', fontSize: 13, borderRadius: 6, border: '1px solid #ddd', outline: 'none' }}
                />
                <button onClick={() => {
                  if (!newNote.trim()) return;
                  axiosInstance.post(`/sponsor/coaching/${agentId}/notes`, { text: newNote.trim(), type: noteType }).then(({ data }) => {
                    if (data.success) { setNotes(prev => [data.note, ...prev]); setNewNote(''); toast.success('Note saved'); }
                  }).catch(() => toast.error('Failed to save'));
                }} style={{ padding: '8px 16px', fontSize: 12, fontWeight: 700, borderRadius: 6, border: 'none', background: colors.navy, color: '#fff', cursor: 'pointer' }}>
                  Save
                </button>
              </div>
            </div>

            {/* Activity Timeline */}
            {notes.length === 0 && <div style={{ color: '#888', fontSize: 13, textAlign: 'center', padding: 20 }}>No notes yet. Log your first touchpoint with {agentName}.</div>}
            {notes.map((n, i) => {
              const icons: any = { call: '📞', meeting: '☕', email: '✉️', thankyou: '💌', note: '📝' };
              return (
                <div key={n.id || i} style={{ display: 'flex', gap: 12, padding: '10px 0', borderBottom: i < notes.length - 1 ? '1px solid #f0f0f0' : 'none' }}>
                  <div style={{ fontSize: 18, width: 32, textAlign: 'center', flexShrink: 0 }}>{icons[n.type] || '📝'}</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: 13, color: '#333', lineHeight: 1.5 }}>{n.text}</div>
                    <div style={{ fontSize: 10, color: '#888', marginTop: 2 }}>
                      {n.type?.replace('thankyou', 'thank you')} · {n.date ? new Date(n.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric', hour: 'numeric', minute: '2-digit' }) : ''}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        )}

        {/* ─── AI STRATEGY CHAT ─── */}
        {subTab === 'chat' && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 12px' }}>AI Strategy Chat — {agentName}</h4>
            <div style={{ maxHeight: 400, overflowY: 'auto', marginBottom: 12, background: '#f8f9fa', borderRadius: 8, padding: 12 }}>
              {coachChat.length === 0 && <div style={{ color: '#888', fontSize: 12, textAlign: 'center', padding: 24 }}>Ask AI about {agentName}'s business, coaching strategy, or growth opportunities</div>}
              {coachChat.map((msg, i) => (
                <div key={i} style={{ marginBottom: 10, display: 'flex', justifyContent: msg.role === 'sponsor' ? 'flex-end' : 'flex-start' }}>
                  <div style={{ maxWidth: '80%', padding: '10px 14px', borderRadius: 12, fontSize: 13, lineHeight: 1.7,
                    background: msg.role === 'sponsor' ? colors.navy : '#fff',
                    color: msg.role === 'sponsor' ? '#fff' : '#333',
                    border: msg.role === 'ai' ? '1px solid #e9ecef' : 'none',
                    whiteSpace: msg.role === 'sponsor' ? 'pre-wrap' : 'normal',
                  }}>{msg.role === 'sponsor' ? msg.text : (
                    <span dangerouslySetInnerHTML={{ __html: formatCoachMarkdown(msg.text) }} />
                  )}</div>
                </div>
              ))}
            </div>
            <div style={{ display: 'flex', gap: 8 }}>
              <input value={chatInput} onChange={e => setChatInput(e.target.value)}
                onKeyDown={e => { if (e.key === 'Enter' && chatInput.trim() && !chatLoading) sendChat(chatInput.trim()); }}
                placeholder={`Ask about ${agentName}'s business...`}
                style={{ flex: 1, padding: '10px 14px', fontSize: 13, borderRadius: 8, border: '1px solid #ddd', outline: 'none' }}
              />
              <button disabled={chatLoading || !chatInput.trim()} onClick={() => sendChat(chatInput.trim())}
                style={{ padding: '10px 20px', fontSize: 13, fontWeight: 700, borderRadius: 8, border: 'none', background: colors.navy, color: '#fff', cursor: 'pointer', opacity: chatLoading ? 0.5 : 1 }}>
                {chatLoading ? '...' : 'Ask'}
              </button>
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginTop: 10 }}>
              {[
                `How do I help ${agentName} grow their listing business?`,
                'What coaching strategy should I use for retention?',
                'How do I deepen this relationship as a lender?',
                `What should I discuss in our next 1:1 meeting?`,
                'How can I help this agent recruit more buyers?',
              ].map((q, i) => (
                <button key={i} onClick={() => setChatInput(q)} style={{ fontSize: 10, padding: '4px 10px', borderRadius: 12, border: '1px solid #e0e0e0', background: '#fafafa', color: '#666', cursor: 'pointer' }}>{q}</button>
              ))}
            </div>
          </div>
        )}

        {/* ─── GAME PLAN ─── */}
        {subTab === 'gameplan' && (
          <div style={{ background: '#fff', borderRadius: 12, padding: 20, border: `1px solid ${colors.border}` }}>
            <h4 style={{ fontSize: 14, fontWeight: 700, color: colors.navy, margin: '0 0 4px' }}>AI Game Plan — {agentName}</h4>
            <p style={{ fontSize: 12, color: '#888', marginBottom: 16 }}>Data-backed coaching action items for your next meeting with this agent.</p>
            {coachPrompts.length > 0 ? (
              <>
                {coachPrompts.map((p: string, i: number) => (
                  <div key={i} style={{ padding: 14, marginBottom: 8, background: '#f8f9fa', borderRadius: 8, borderLeft: `3px solid ${colors.gold}`, fontSize: 13, color: '#333', lineHeight: 1.7 }}>
                    <span style={{ fontWeight: 700, color: colors.navy, marginRight: 8 }}>#{i + 1}</span>{p}
                  </div>
                ))}
                <button onClick={() => { setCoachPrompts([]); axiosInstance.get(`/sponsor/coaching/${agentId}/prompts?refresh=true`).then(({ data }) => { if (data.success) setCoachPrompts(data.prompts || []); }); }}
                  style={{ marginTop: 8, padding: '8px 20px', fontSize: 12, fontWeight: 600, borderRadius: 6, border: `1px solid ${colors.gold}`, background: '#fff', color: colors.gold, cursor: 'pointer' }}>
                  Regenerate Game Plan
                </button>
              </>
            ) : (
              <button onClick={async () => {
                try {
                  const { data } = await axiosInstance.get(`/sponsor/coaching/${agentId}/prompts`);
                  if (data.success) setCoachPrompts(data.prompts || []);
                } catch { }
              }} style={{ padding: '12px 32px', fontSize: 14, fontWeight: 700, borderRadius: 8, border: 'none', cursor: 'pointer', background: `linear-gradient(135deg, ${colors.gold}, ${colors.gold2})`, color: colors.navy }}>
                Generate Game Plan
              </button>
            )}
          </div>
        )}

        {/* Disclaimer */}
        <div style={{ marginTop: 16, fontSize: 10, color: '#999', lineHeight: 1.5, padding: '8px 12px', background: '#fafafa', borderRadius: 4 }}>
          AI-generated coaching recommendations are based on available production data and market analytics. All data access is logged for compliance.
        </div>
      </div>
    </div>
  );
};

export default AgentCoachingDetail;
